// Formatting helpers. All clock times are shown in America/New_York.

const ET = 'America/New_York';
const tHM = new Intl.DateTimeFormat('en-US', { timeZone: ET, hour: 'numeric', minute: '2-digit' });
const tHMS = new Intl.DateTimeFormat('en-US', { timeZone: ET, hour: 'numeric', minute: '2-digit', second: '2-digit' });
const tDay = new Intl.DateTimeFormat('en-US', { timeZone: ET, month: 'short', day: 'numeric' });
const tFull = new Intl.DateTimeFormat('en-US', { timeZone: ET, weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const nInt = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const nCompact = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });

export const parseTime = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
};

export function fmtTime(iso: string | number | null | undefined, seconds = false): string {
  const t = typeof iso === 'number' ? iso : parseTime(iso);
  if (t === null) return '—';
  return `${(seconds ? tHMS : tHM).format(t)} ET`;
}
export function fmtDateTime(iso: string | null | undefined): string {
  const t = parseTime(iso);
  return t === null ? '—' : `${tFull.format(t)} ET`;
}
/** "8:42 PM" today, "Nov 3, 8:42 PM" otherwise (relative to `now`). */
export function fmtStamp(iso: string | null | undefined, now: number): string {
  const t = parseTime(iso);
  if (t === null) return '—';
  return tDay.format(t) === tDay.format(now) ? tHM.format(t) : `${tDay.format(t)}, ${tHM.format(t)}`;
}

export const fmtInt = (n: number | null | undefined): string => (n === null || n === undefined || !Number.isFinite(n) ? '—' : nInt.format(Math.round(n)));
export const fmtCompact = (n: number | null | undefined): string => (n === null || n === undefined ? '—' : Math.abs(n) < 10_000 ? nInt.format(n) : nCompact.format(n));
export const fmtPct = (p: number | null | undefined, digits = 1): string => (p === null || p === undefined ? '—' : `${p.toFixed(digits)}%`);
export const fmtRatioPct = (r: number | null | undefined): string => (r === null || r === undefined ? '—' : `${Math.round(r * 100)}%`);
export const fmtSigned = (x: number | null | undefined, digits = 1): string => (x === null || x === undefined ? '—' : `${x > 0 ? '+' : x < 0 ? '−' : '±'}${Math.abs(x).toFixed(digits)}`);

/** Probability 0–1 as a whole-percent string; never rounds to a false certainty. */
export function fmtProb(p: number | null | undefined): string {
  if (p === null || p === undefined) return '—';
  if (p >= 0.995) return '>99%';
  if (p <= 0.005) return '<1%';
  return `${Math.round(p * 100)}%`;
}

// ------------------------------------------------------------------ countdown to polls closing

/** Whole days/hours/minutes/seconds left (seconds rounded up, so it never reads 0:00 while polls are still open). */
export function countdownParts(ms: number): { d: number; h: number; m: number; s: number } {
  const t = Math.max(0, Math.ceil(ms / 1000));
  return { d: Math.floor(t / 86_400), h: Math.floor((t % 86_400) / 3600), m: Math.floor((t % 3600) / 60), s: t % 60 };
}
/** Compact countdown for the top bar: "2d 3h" → "5h 12m" → "12:34". */
export function countdownShort(ms: number): string {
  const { d, h, m, s } = countdownParts(ms);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}:${String(s).padStart(2, '0')}`;
}
const tCloseDay = new Intl.DateTimeFormat('en-US', { timeZone: ET, weekday: 'long', month: 'short', day: 'numeric' });
/** "Tuesday, Nov. 3 at 7:00 PM ET" (AP style month abbreviations). */
export function fmtCloseTime(t: number): string {
  const day = tCloseDay.format(t).replace(/\b(Jan|Feb|Aug|Sep|Oct|Nov|Dec)\b/, '$1.').replace('Sep.', 'Sept.');
  return `${day} at ${tHM.format(t)} ET`;
}
